import { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { AccessTokenContext, PortalIdTokenContext } from '../auth/authContexts';
import type {
  PoliciesResponse,
  ApiError,
  User,
  PolicyLink,
  AlterationResult,
  HealthResponse,
  PaymentMethodStartResponse,
  PaymentMethodOtpChannel,
  PaymentMethodVerifyResponse,
  PaymentMethodSimpleResponse,
  LinkPolicyResponse,
  ResendPolicyLinkOtpResponse,
  VerifyPolicyLinkOtpResponse,
  SyncMfaPhoneResponse,
  SubmitWindscreenClaimPayload,
  SubmitWindscreenClaimResponse,
  SubmitMultiVehicleClaimPayload,
  SubmitMultiVehicleClaimResponse,
  SubmitWeatherClaimPayload,
  SubmitWeatherClaimResponse,
  SubmitTheftClaimPayload,
  SubmitTheftClaimResponse,
  SubmitFireClaimPayload,
  SubmitFireClaimResponse,
  SubmitMaliciousDamageClaimPayload,
  SubmitMaliciousDamageClaimResponse,
  SubmitOtherLossClaimPayload,
  SubmitOtherLossClaimResponse,
  SubmitSingleVehicleClaimPayload,
  SubmitSingleVehicleClaimResponse,
  SubmittedClaimsResponse,
  SubmittedClaimStatusResponse,
} from './types';
import {
  isDevMode,
  getMockUser,
  applyDevMockPolicyUpdate,
} from '../utils/devMode';
import {
  clientLogDebug,
  clientLogError,
  clientLogInfo,
  clientLogInfoOptions,
  clientLogWarn,
} from '../utils/clientLog';
import { PAYMENT_METHOD_CSRF_HEADER } from '../constants/paymentMethodCsrf';
import { getPortalCsrfToken, setPortalCsrfToken } from '../utils/portalCsrfStore';
import { PORTAL_BLOCKED_CODES } from '../utils/policyReferralState';
import {
  resolveApiProxyTimeoutMs,
  resolveLinkPolicyClientTimeoutMs,
} from '../../shared/apiProxyTimeout.js';

/** Lazy-load local scenario harness — only referenced under `import.meta.env.DEV` branches (omitted from prod builds). */
async function loadDevPolicyScenarios(): Promise<
  typeof import('../dev/policyScenarios/mockPolicies')
> {
  return import('../dev/policyScenarios/mockPolicies');
}

/** Async accessor for Auth0 access tokens (wired via React context in production). */
export type AccessTokenGetter = () => Promise<string | null>;

export type { HealthResponse, PolicySystemSnapshot, PolicySystemStatus } from './types';

// API base URL - will be /api in production
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * SPA link-policy abort timeout: strictly below App Service proxy timeout.
 * Optional `VITE_API_PROXY_TIMEOUT_MS` should match web app `API_PROXY_TIMEOUT_MS` when overridden.
 * Pass `''` when unset so resolveApiProxyTimeoutMs never falls back to server-only process.env.
 */
const LINK_POLICY_CLIENT_TIMEOUT_MS = resolveLinkPolicyClientTimeoutMs(
  resolveApiProxyTimeoutMs(import.meta.env.VITE_API_PROXY_TIMEOUT_MS ?? ''),
);
function mergePaymentMethodCsrfHeader(
  initHeaders: HeadersInit | undefined,
  csrfToken: string | null | undefined
): HeadersInit {
  if (!csrfToken?.trim()) {
    return initHeaders ?? {};
  }
  return {
    ...(initHeaders as Record<string, string>),
    [PAYMENT_METHOD_CSRF_HEADER]: csrfToken.trim(),
  };
}

/** Credentialed payment-method API call (session cookie + CSRF header). */
async function paymentMethodApiRequest<T>(
  endpoint: string,
  init: RequestInit,
  getToken?: AccessTokenGetter,
  /** Payment-method session CSRF; falls back to portal CSRF (e.g. start). */
  csrfToken?: string | null
): Promise<T> {
  const headerToken = csrfToken ?? getPortalCsrfToken();
  return apiRequest<T>(
    endpoint,
    {
      ...init,
      credentials: 'include',
      headers: mergePaymentMethodCsrfHeader(init.headers, headerToken),
    },
    getToken
  );
}

const MUTATING_API_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * Duplicate Bearer on same-origin `/api/*` (Linux: Express proxy). If anything upstream strips
 * `Authorization`, `server.js` still forwards the token to Azure Functions using this header.
 */
const PORTAL_PROXY_AUTH_HEADER = 'X-Portal-Authorization';
const PORTAL_ID_TOKEN_HEADER = 'X-Portal-Id-Token';
const CORRELATION_HEADER = 'x-correlation-id';

/** Wired from `useApi` so authenticated requests can send the Auth0 ID token for MFA checks. */
let portalIdTokenGetter: (() => Promise<string | null>) | undefined;

export function registerPortalIdTokenGetter(getter: (() => Promise<string | null>) | undefined) {
  portalIdTokenGetter = getter;
}

/** Clears the module getter only when it still matches this instance's registration. */
export function unregisterPortalIdTokenGetter(getter: (() => Promise<string | null>) | undefined) {
  if (portalIdTokenGetter === getter) {
    portalIdTokenGetter = undefined;
  }
}

async function attachPortalIdTokenHeader(headers: HeadersInit): Promise<void> {
  if (!portalIdTokenGetter) return;
  try {
    const idToken = await portalIdTokenGetter();
    if (idToken) {
      (headers as Record<string, string>)[PORTAL_ID_TOKEN_HEADER] = idToken;
    }
  } catch {
    /* non-fatal */
  }
}

function createCorrelationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `client-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export { isPolicySystemUnavailableApiError } from '../hooks/useApiError';

/**
 * Combine an optional caller AbortSignal with the request timeout signal.
 * Uses AbortSignal.any when available; otherwise a local controller that aborts
 * when either source aborts (and exposes dispose to detach listeners).
 */
export function composeRequestAbortSignal(
  externalSignal: AbortSignal | undefined,
  timeoutSignal: AbortSignal,
): { signal: AbortSignal; dispose: () => void } {
  if (!externalSignal) {
    return { signal: timeoutSignal, dispose: () => undefined };
  }
  if (typeof AbortSignal.any === 'function') {
    return {
      signal: AbortSignal.any([externalSignal, timeoutSignal]),
      dispose: () => undefined,
    };
  }
  const combined = new AbortController();
  const forwardAbort = () => {
    if (!combined.signal.aborted) {
      combined.abort();
    }
  };
  if (externalSignal.aborted || timeoutSignal.aborted) {
    forwardAbort();
    return { signal: combined.signal, dispose: () => undefined };
  }
  externalSignal.addEventListener('abort', forwardAbort);
  timeoutSignal.addEventListener('abort', forwardAbort);
  return {
    signal: combined.signal,
    dispose: () => {
      externalSignal.removeEventListener('abort', forwardAbort);
      timeoutSignal.removeEventListener('abort', forwardAbort);
    },
  };
}

/**
 * Resolve Bearer access token for API calls (Auth0 silent token).
 */
async function getBearerAccessToken(getToken?: AccessTokenGetter): Promise<string | null> {
  if (!getToken) return null;
  try {
    const token = await getToken();
    if (!token) {
      clientLogError('[Auth] Bearer token grant denied or unavailable. No access token returned.');
      return null;
    }
    return token;
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : String(error);
    clientLogError('[Auth] Bearer token grant denied or unavailable.', msg);
    return null;
  }
}

/**
 * Make authenticated API request with timeout and error handling
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  getToken?: AccessTokenGetter,
  /** Default 30s; FNOL submit can block longer on Entsia sync. */
  timeoutMs: number = 30000
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  // Add timeout to prevent hanging requests (compose with caller AbortSignal when present)
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
  const externalSignal = options.signal;
  const { signal, dispose: disposeAbortListeners } = composeRequestAbortSignal(
    externalSignal ?? undefined,
    timeoutController.signal,
  );
  const clearRequestTimeout = () => {
    clearTimeout(timeoutId);
    disposeAbortListeners();
  };
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    [CORRELATION_HEADER]: createCorrelationId(),
    ...options.headers,
  };

  // Add access token if available
  if (getToken) {
    const token = await getBearerAccessToken(getToken);
    if (token) {
      const bearer = `Bearer ${token}`;
      const h = headers as Record<string, string>;
      h['Authorization'] = bearer;
      h[PORTAL_PROXY_AUTH_HEADER] = bearer;
    } else {
      clientLogError('[Auth] Bearer token grant denied or unavailable. Request may be rejected (401/403).');
    }
  }

  await attachPortalIdTokenHeader(headers);

  const reqMethod = (options.method ?? 'GET').toUpperCase();
  if (MUTATING_API_METHODS.has(reqMethod)) {
    const portalCsrf = getPortalCsrfToken();
    if (portalCsrf) {
      const h = headers as Record<string, string>;
      if (!h[PAYMENT_METHOD_CSRF_HEADER]) {
        h[PAYMENT_METHOD_CSRF_HEADER] = portalCsrf;
      }
    }
  }
  const logClaimsDebug =
    endpoint === '/claims/windscreen' ||
    endpoint === '/claims/multi-vehicle' ||
    endpoint === '/claims/single-vehicle';
  if (logClaimsDebug) {
    const hasBearer = !!(headers as Record<string, string>)['Authorization'];
    clientLogInfo('[Claims][API] request start', {
      method: reqMethod,
      url,
      timeoutSec: Math.round(timeoutMs / 1000),
      hasBearerToken: hasBearer,
    });
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
      // Send HttpOnly portal cookies (activity session, payment-method session) on same-origin /api.
      credentials: options.credentials ?? 'include',
      signal,
    });

    clearRequestTimeout();

    if (logClaimsDebug) {
      clientLogInfo('[Claims][API] response headers', {
        status: response.status,
        ok: response.ok,
        contentType: response.headers.get('content-type') ?? '(none)',
      });
    }

    if (response.status === 204) {
      return undefined as T;
    }

    // Check if response is HTML (likely a redirect or error page)
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      const text = await response.text();
      clientLogError('[API] Unexpected response. Check server configuration.', {
        contentType,
        snippet: text.substring(0, 150),
      });

      if (response.status === 401 || response.status === 403) {
        clientLogError('[Auth] Session expired. Sign in again.', response.status);
        const authError = new Error('Authentication required. Please sign in again.') as Error & { status?: number };
        authError.status = response.status;
        throw authError;
      }
      
      // If it's HTML, likely a routing issue
      if (contentType.includes('text/html')) {
        throw new Error(`API endpoint returned HTML instead of JSON. Check that the API function is deployed correctly. URL: ${url}`);
      }
      
      throw new Error(`Unexpected response type: ${contentType}`);
    }

    if (!response.ok) {
      // For getPolicy endpoint, if 404, throw a specific error that can be caught
      if (
        response.status === 404 &&
        endpoint.includes('/policies/') &&
        !endpoint.endsWith('/policies') &&
        !endpoint.includes('/payment-method/')
      ) {
        const error = new Error('Policy endpoint not found (404)') as Error & { status: number };
        error.status = 404;
        throw error;
      }
      
      // link-policy 404: explain that the route was reached and failure is from Entsia flow, not a missing route
      if (response.status === 404 && endpoint.includes('link-policy')) {
        const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
        const msg =
          (typeof body.message === 'string' ? body.message : null) ||
          (typeof body.error === 'string' ? body.error : null) ||
          'Policy not found or insurer rejected the request.';
        const codeStr = typeof body.code === 'string' ? body.code : undefined;
        clientLogDebug(
          '[API] link-policy 404: route reached; insurer may not have found the policy.',
          { msg, code: codeStr }
        );
        clientLogError('[API] Policy linking failed.', { status: 404, code: codeStr });
        const err = new Error(msg) as Error & { apiBody?: Record<string, unknown>; status?: number };
        err.apiBody = body;
        err.status = 404;
        throw err;
      }

      // Only GET /policies (list) uses empty data on 404 — never swallow 404 for POST/claims/etc.
      if (response.status === 404 && endpoint === '/policies' && reqMethod === 'GET') {
        clientLogWarn('[API] GET /policies returned 404; returning empty policy list.', endpoint);
        return { policies: [], firstName: undefined } as T;
      }

      const errorData: ApiError & { mismatches?: string[]; faultReference?: string; downstreamCode?: string; downstreamMessage?: string; failedStep?: string } = await response.json().catch(() => ({
        error: 'Request failed',
        message: `HTTP ${response.status}: ${response.statusText}`,
      }));

      const message = errorData.message || errorData.error || 'Request failed';
      const stepHint = errorData.failedStep ? ` [step: ${errorData.failedStep}]` : '';
      const status = response.status;
      if (errorData.code === 'POLICY_SYSTEM_UNAVAILABLE') {
        clientLogError('[API] Policy system unavailable (503).', { message, correlationId: errorData.correlationId ?? '' });
      } else if (endpoint.includes('link-policy')) {
        clientLogDebug('[API] link-policy error (insurer flow).');
      }
      const isBearerTokenDeniedByInsurer = errorData.code === 'BEARER_TOKEN_DENIED' || /unauthorized|invalid token|token denied|access denied|401|403/i.test([errorData.downstreamMessage, errorData.downstreamCode, message].filter(Boolean).join(' '));
      const hasDownstreamInfo = errorData.code === 'DOWNSTREAM_ERROR' || errorData.downstreamCode || errorData.downstreamMessage || errorData.faultReference || errorData.code === 'POLICY_NOT_FOUND' || /insurer|policy with that number|entsia|downstream/i.test(message);

      if (status === 401 || status === 403) {
        if (errorData.code !== 'POLICY_REFERRAL' && errorData.code !== 'MFA_REQUIRED') {
          const isPaymentMethod = endpoint.includes('/payment-method/');
          if (errorData.code === 'CSRF_VALIDATION_FAILED') {
            clientLogError(
              '[API] Payment method CSRF failed. Refresh the update payment page and try again.',
              message
            );
          } else {
            const sessionLike =
              isPaymentMethod &&
              /session has expired|session expired|success.?false/i.test(
                [message, (errorData as { error?: string }).error].filter(Boolean).join(' ')
              );
            if (sessionLike) {
              clientLogError(
                '[API] Payment method session expired. Start update payment method again.',
                message
              );
            } else {
              clientLogError('[Auth] Session expired. Sign in again.', status);
            }
          }
        }
      } else if (isBearerTokenDeniedByInsurer) {
        clientLogError('[Auth] Bearer token grant denied by insurer. Sign in again or contact support.');
      } else if (hasDownstreamInfo) {
        clientLogError('[API] Request failed. Contact support if this continues.', {
          status,
          code: errorData.code,
          downstreamCode: errorData.downstreamCode,
          failedStep: errorData.failedStep,
        });
      } else if (status === 504) {
        clientLogError('[API] Request timed out (504). Backend or downstream (Entsia) may be slow or unreachable.');
      } else if (status === 502) {
        clientLogError('[API] Gateway error (502). Backend unreachable or downstream (Entsia) failed.', message);
      } else {
        clientLogError(`[API] Request failed (${status}).`, {
          message,
          code: errorData.code,
        });
      }

      const err = new Error(stepHint ? `${message}${stepHint}` : message) as Error & { apiBody?: typeof errorData; status?: number };
      err.apiBody = errorData;
      err.status = status;
      if (logClaimsDebug) {
        clientLogWarn('[Claims][API] error response body (summary)', {
          httpStatus: status,
          message,
          error: errorData.error,
          code: errorData.code,
          downstreamCode: errorData.downstreamCode,
        });
      }
      throw err;
    }

    const jsonBody = await response.json();
    if (logClaimsDebug) {
      const preview =
        jsonBody && typeof jsonBody === 'object'
          ? {
              ok: (jsonBody as { ok?: unknown }).ok,
              claimNumber: (jsonBody as { claimNumber?: unknown }).claimNumber,
              httpStatus: (jsonBody as { httpStatus?: unknown }).httpStatus,
              entsiaHttpStatus: (jsonBody as { entsiaHttpStatus?: unknown }).entsiaHttpStatus,
              error: (jsonBody as { error?: unknown }).error,
              message: (jsonBody as { message?: unknown }).message,
            }
          : jsonBody;
      clientLogInfo('[Claims][API] response JSON (summary)', preview);
    }
    return jsonBody;
  } catch (error) {
    clearRequestTimeout();

    if (endpoint === '/claims/windscreen') {
      clientLogError('[Claims][API] request failed or threw', error);
    }

    // Handle abort/timeout gracefully (caller abort ≠ timeout)
    if (error instanceof Error && error.name === 'AbortError') {
      if (externalSignal?.aborted) {
        throw error;
      }
      clientLogError(`[API] Request timed out (${timeoutMs / 1000}s). Server may be slow or unreachable.`);
      const timeoutError = new Error(
        `Request timed out after ${timeoutMs / 1000} seconds. The server may be slow or unavailable.`
      );
      timeoutError.name = 'TimeoutError';
      throw timeoutError;
    }
    
    throw error;
  }
}

/** True when GET policy returned referral block (not an auth/session failure). */
export function isPolicyReferralError(err: unknown): boolean {
  const e = err as Error & { status?: number; apiBody?: { code?: string } } | undefined;
  return e?.status === 403 && e?.apiBody?.code === 'POLICY_REFERRAL';
}

/** Referral, legacy multi-vehicle, or other portal block — must not be treated as auth failure. */
export function isPolicyPortalBlockedError(err: unknown): boolean {
  const e = err as Error & { status?: number; apiBody?: { code?: string } } | undefined;
  const c = e?.apiBody?.code;
  return e?.status === 403 && c != null && PORTAL_BLOCKED_CODES.has(c);
}

/** True when the error is from a 401/403 API response (session expired or denied). Callers can trigger logoutRedirect. */
export function isAuthError(err: unknown): err is Error & { status: number } {
  const e = err as Error & { status?: number; apiBody?: { code?: string } } | undefined;
  if (e == null) return false;
  if (isPolicyPortalBlockedError(e)) return false;
  if (e.apiBody?.code === 'CSRF_VALIDATION_FAILED' || e.apiBody?.code === 'MFA_REQUIRED') return false;
  return e.status === 401 || e.status === 403;
}

const DEV_MOCK_CLAIM_RESPONSE = {
  ok: true as const,
  claimNumber: 'DEV-CLAIM-001',
  httpStatus: 200,
  response: { mock: true },
  entsiaHttpStatus: 200,
  entsiaResponse: { mock: true },
  claimsBackend: 'entsia' as const,
};

async function maybeDevMockClaimSubmit(policyRef: string): Promise<typeof DEV_MOCK_CLAIM_RESPONSE | null> {
  if (!import.meta.env.DEV || !isDevMode()) return null;
  const { assertDevMockClaimAllowed } = await loadDevPolicyScenarios();
  await assertDevMockClaimAllowed(policyRef);
  clientLogDebug('[API] claim submit: dev mock (matrix gate passed)', policyRef);
  return DEV_MOCK_CLAIM_RESPONSE;
}

/**
 * API service functions
 */
export const api = {
  /**
   * Get current user
   */
  async getUser(getToken?: AccessTokenGetter): Promise<User> {
    if (isDevMode()) {
      const user = getMockUser();
      setPortalCsrfToken(user.csrfToken ?? 'dev-mock-portal-csrf');
      return user;
    }
    const user = await apiRequest<User>('/user', { method: 'GET' }, getToken);
    setPortalCsrfToken(user.csrfToken ?? null);
    return user;
  },

  /**
   * Record server-side logout so previously issued access tokens are rejected by the API.
   */
  async portalLogout(getToken?: AccessTokenGetter): Promise<void> {
    if (isDevMode()) {
      return;
    }
    await apiRequest<void>('/logout', { method: 'POST' }, getToken);
  },

  /**
   * Portal + policy-system health (anonymous; no auth required).
   */
  async getHealth(): Promise<HealthResponse> {
    if (isDevMode()) {
      return { ok: true, entsiaConnected: true, policySystem: { status: 'ok' } };
    }
    return apiRequest<HealthResponse>('/health', { method: 'GET' });
  },

  /**
   * Get user's policies (minimal data for Dashboard)
   */
  async getPolicies(getToken?: AccessTokenGetter): Promise<PoliciesResponse> {
    if (import.meta.env.DEV && isDevMode()) {
      const { getMockPoliciesWithAccess } = await loadDevPolicyScenarios();
      const result = await getMockPoliciesWithAccess();
      clientLogDebug('[API] getPolicies: dev mock (matrix)', {
        policiesCount: result.policies?.length ?? 0,
        firstName: result.firstName ?? '(none)',
      });
      return result;
    }
    const url = `${API_BASE_URL}/policies`;
    clientLogDebug('[API] getPolicies: GET', url);
    try {
      const result = await apiRequest<PoliciesResponse>('/policies', { method: 'GET' }, getToken);
      clientLogDebug('[API] getPolicies response:', {
        policiesCount: result.policies?.length ?? 0,
        firstName: result.firstName ?? '(none)',
      });
      return result;
    } catch (err) {
      clientLogError('[API] getPolicies failed:', err);
      throw err;
    }
  },

  /**
   * Get full policy details (for PolicyView)
   */
  async getPolicy(policyRef: string, getToken?: AccessTokenGetter): Promise<PolicyLink> {
    if (import.meta.env.DEV && isDevMode()) {
      const { getMockPolicyWithAccess } = await loadDevPolicyScenarios();
      return getMockPolicyWithAccess(policyRef);
    }
    return apiRequest<PolicyLink>(`/policies/${encodeURIComponent(policyRef)}`, { method: 'GET' }, getToken);
  },

  /**
   * Submit windscreen/glass FNOL to Entsia (server builds payload and POSTs private claims URL).
   * Longer timeout: Entsia sync create can exceed 30s.
   */
  async submitWindscreenClaim(
    body: SubmitWindscreenClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitWindscreenClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    const serialized = JSON.stringify(body);
    const incRaw = body.incidentDateDdMmYyyy;
    const incTrim = typeof incRaw === 'string' ? incRaw.trim() : '';
    /** One string so production consoles show it without expanding collapsed objects. */
    clientLogInfo(
      `[Claims][API] FNOL wire: incidentDateDdMmYyyy type=${typeof incRaw} len=${incTrim.length} hasKeyInJson=${serialized.includes('"incidentDateDdMmYyyy"')} wireBytes=${serialized.length} keys=${Object.keys(body as object).sort().join(',')}`
    );
    const logFullWirePayload =
      import.meta.env.DEV ||
      String(import.meta.env.VITE_LOG_WINDSCREEN_FNOL_PAYLOAD ?? '').toLowerCase() === 'true';
    if (logFullWirePayload) {
      clientLogInfo(
        '[Claims][API] submitWindscreenClaim outbound JSON (PII — disable in shared prod builds)',
        serialized,
        clientLogInfoOptions(true)
      );
    }
    clientLogInfo('[Claims][API] submitWindscreenClaim payload summary', {
      policyRef: body.policyRef,
      productRef: body.productRef ?? null,
      policyType: body.policyType ?? null,
      descriptionChars: body.description?.length ?? 0,
      repairOption: body.repairOption,
      smallChip: body.smallChip,
      attachmentCount: body.attachmentFileNames?.length ?? 0,
      requiresTowing: body.requiresTowing ?? null,
      gstRegistered: body.gstRegistered ?? null,
      abnDigits: body.abn?.replace(/\D/g, '').length ?? 0,
      itcEntitled: body.itcEntitled ?? null,
      itcPercentage: body.itcPercentage ?? null,
      alreadyReplaced: body.alreadyReplaced ?? null,
      hasIncidentDate: !!body.incidentDateDdMmYyyy?.trim(),
      incidentDateDdMmYyyyChars: body.incidentDateDdMmYyyy?.trim().length ?? 0,
      hasIncidentTime: !!(body.incidentTimeHm?.trim() && body.incidentTimeAmPm),
      incidentTimeHmChars: body.incidentTimeHm?.trim().length ?? 0,
      incidentTimeAmPm: body.incidentTimeAmPm ?? null,
      incidentAddressUnknown: body.incidentAddressUnknown ?? null,
      incidentAddressSearchChars: (body.incidentAddressSearch ?? '').trim().length,
      incidentStreetChars: (body.incidentStreet ?? '').trim().length,
      incidentSuburbChars: (body.incidentSuburb ?? '').trim().length,
      incidentPostcodeChars: (body.incidentPostcode ?? '').trim().length,
      incidentStateChars: (body.incidentState ?? '').trim().length,
      topLevelKeys: Object.keys(body as object).sort(),
      wireBytes: serialized.length,
    });
    const data = await apiRequest<SubmitWindscreenClaimResponse>(
      '/claims/windscreen',
      { method: 'POST', body: serialized },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitWindscreenClaimResponse).ok !== true) {
      clientLogError('[API] submitWindscreenClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/windscreen — the Function App may need deploying or the route may be missing.'
      );
    }
    return data;
  },

  /**
   * Submit multi-vehicle collision FNOL to Entsia (same timeout as windscreen).
   */
  async submitMultiVehicleClaim(
    body: SubmitMultiVehicleClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitMultiVehicleClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    clientLogInfo('[Claims][API] submitMultiVehicleClaim payload summary', {
      policyRef: body.policyRef,
      productRef: body.productRef ?? null,
      policyType: body.policyType ?? null,
      descriptionChars: body.description?.length ?? 0,
      requiresTowing: body.requiresTowing ?? null,
    });
    const data = await apiRequest<SubmitMultiVehicleClaimResponse>(
      '/claims/multi-vehicle',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitMultiVehicleClaimResponse).ok !== true) {
      clientLogError('[API] submitMultiVehicleClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/multi-vehicle — the Function App may need deploying or the route may be missing.'
      );
    }
    return data;
  },

  /**
   * Submit weather FNOL to Entsia (structured payload; same timeout as windscreen).
   */
  async submitWeatherClaim(
    body: SubmitWeatherClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitWeatherClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    clientLogInfo('[Claims][API] submitWeatherClaim payload summary', {
      policyRef: body.policyRef,
      productRef: body.productRef ?? null,
      policyType: body.policyType ?? null,
      descriptionChars: body.description?.length ?? 0,
      weatherConditionsChars: body.weatherConditionsDescription?.length ?? 0,
      hasIncidentDate: !!body.incidentDateDdMmYyyy?.trim(),
    });
    const data = await apiRequest<SubmitWeatherClaimResponse>(
      '/claims/weather',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitWeatherClaimResponse).ok !== true) {
      clientLogError('[API] submitWeatherClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/weather — the Function App may need deploying or the route may be missing.'
      );
    }
    return data;
  },

  /**
   * Submit theft FNOL to Entsia (structured payload; same timeout as windscreen).
   */
  async submitTheftClaim(
    body: SubmitTheftClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitTheftClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    clientLogInfo('[Claims][API] submitTheftClaim payload summary', {
      policyRef: body.policyRef,
      productRef: body.productRef ?? null,
      policyType: body.policyType ?? null,
      descriptionChars: body.description?.length ?? 0,
      theftDetailChars: body.theftOrBreakInDetailDescription?.length ?? 0,
      hasIncidentDate: !!body.incidentDateDdMmYyyy?.trim(),
    });
    const data = await apiRequest<SubmitTheftClaimResponse>(
      '/claims/theft',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitTheftClaimResponse).ok !== true) {
      clientLogError('[API] submitTheftClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/theft — the Function App may need deploying or the route may be missing.'
      );
    }
    return data;
  },

  /**
   * Submit fire FNOL to Entsia (structured payload; same timeout as windscreen).
   */
  async submitFireClaim(
    body: SubmitFireClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitFireClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    clientLogInfo('[Claims][API] submitFireClaim payload summary', {
      policyRef: body.policyRef,
      productRef: body.productRef ?? null,
      policyType: body.policyType ?? null,
      descriptionChars: body.description?.length ?? 0,
      fireTypeChars: body.fireClaimTypeDescription?.length ?? 0,
      hasIncidentDate: !!body.incidentDateDdMmYyyy?.trim(),
    });
    const data = await apiRequest<SubmitFireClaimResponse>(
      '/claims/fire',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitFireClaimResponse).ok !== true) {
      clientLogError('[API] submitFireClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/fire — the Function App may need deploying or the route may be missing.'
      );
    }
    return data;
  },

  /**
   * Submit malicious-damage FNOL to Entsia (structured payload; same timeout as fire).
   */
  async submitMaliciousDamageClaim(
    body: SubmitMaliciousDamageClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitMaliciousDamageClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    const data = await apiRequest<SubmitMaliciousDamageClaimResponse>(
      '/claims/malicious-damage',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitMaliciousDamageClaimResponse).ok !== true) {
      clientLogError('[API] submitMaliciousDamageClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/malicious-damage.'
      );
    }
    return data;
  },

  /**
   * Submit other-loss FNOL to Entsia (structured payload; same timeout as fire).
   */
  async submitOtherLossClaim(
    body: SubmitOtherLossClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitOtherLossClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    const data = await apiRequest<SubmitOtherLossClaimResponse>(
      '/claims/other-loss',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitOtherLossClaimResponse).ok !== true) {
      clientLogError('[API] submitOtherLossClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/other-loss.'
      );
    }
    return data;
  },

  /**
   * Submit single-vehicle collision FNOL to Entsia (same timeout as windscreen / multi-vehicle).
   */
  async submitSingleVehicleClaim(
    body: SubmitSingleVehicleClaimPayload,
    getToken?: AccessTokenGetter
  ): Promise<SubmitSingleVehicleClaimResponse> {
    const mock = await maybeDevMockClaimSubmit(body.policyRef);
    if (mock) return mock;
    const legacyDescription = (body as SubmitSingleVehicleClaimPayload & { description?: string })
      .description;
    clientLogInfo('[Claims][API] submitSingleVehicleClaim payload summary', {
      policyRef: body.policyRef,
      productRef: body.productRef ?? null,
      policyType: body.policyType ?? null,
      incidentDescriptionChars: body.incidentDescription?.length ?? 0,
      vehicleDamageDescriptionChars: body.vehicleDamageDescription?.length ?? 0,
      legacyDescriptionChars: legacyDescription?.length ?? 0,
    });
    const data = await apiRequest<SubmitSingleVehicleClaimResponse>(
      '/claims/single-vehicle',
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      120000
    );
    if (!data || typeof data !== 'object' || (data as SubmitSingleVehicleClaimResponse).ok !== true) {
      clientLogError('[API] submitSingleVehicleClaim: unexpected response shape', data);
      throw new Error(
        'Claim submission failed (unexpected response). Check the Network tab for POST /api/claims/single-vehicle — the Function App may need deploying or the route may be missing.'
      );
    }
    return data;
  },

  /**
   * Claim numbers persisted after successful portal FNOL (`GET /api/claims/submitted`).
   */
  async getSubmittedClaims(getToken?: AccessTokenGetter): Promise<SubmittedClaimsResponse> {
    if (isDevMode()) {
      return { claims: [] };
    }
    return apiRequest<SubmittedClaimsResponse>('/claims/submitted', { method: 'GET' }, getToken);
  },

  /**
   * Live Wilbur status for a portal-submitted claim (`GET /api/claims/submitted/{claimNumber}/status`).
   */
  async getSubmittedClaimStatus(
    claimNumber: string,
    getToken?: AccessTokenGetter,
  ): Promise<SubmittedClaimStatusResponse> {
    const encoded = encodeURIComponent(claimNumber.trim());
    return apiRequest<SubmittedClaimStatusResponse>(
      `/claims/submitted/${encoded}/status`,
      { method: 'GET' },
      getToken,
    );
  },

  /**
   * Link a policy to the current user (onboarding). Verifies details against Entsia and creates PolicyLink.
   * Supports one or more policy numbers and returns linked + failed outcomes.
   */
  async linkPolicy(
    body: { firstName: string; lastName: string; email: string; policyNumbers: string[] },
    getToken?: AccessTokenGetter
  ): Promise<LinkPolicyResponse> {
    // Entsia + ACS can exceed 30s; timeout stays strictly below App Service proxy (see shared/apiProxyTimeout.js).
    return apiRequest<LinkPolicyResponse>(
      '/link-policy',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      getToken,
      LINK_POLICY_CLIENT_TIMEOUT_MS
    );
  },

  async verifyPolicyLinkOtp(
    body: { challengeId: string; code: string },
    getToken?: AccessTokenGetter
  ): Promise<VerifyPolicyLinkOtpResponse> {
    return apiRequest<VerifyPolicyLinkOtpResponse>(
      '/link-policy/verify-otp',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      getToken
    );
  },

  async resendPolicyLinkOtp(
    body: { challengeId: string },
    getToken?: AccessTokenGetter
  ): Promise<ResendPolicyLinkOtpResponse> {
    return apiRequest<ResendPolicyLinkOtpResponse>(
      '/link-policy/resend-otp',
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
      getToken
    );
  },

  async syncMfaPhone(getToken?: AccessTokenGetter): Promise<SyncMfaPhoneResponse> {
    return apiRequest<SyncMfaPhoneResponse>(
      '/mfa/sync-phone',
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      getToken
    );
  },

  /**
   * Update policy (alteration flow). POST to /api/policies/{policyRef}/update.
   */
  async updateRegistrationNumber(
    policyRef: string,
    body: {
      registrationNumber: string;
      state?: string;
      parkingAddress?: string;
      overnightParking?: string;
      modificationsValue?: number;
      offRoadRecovery?: string;
      firstName?: string;
      surname?: string;
      email?: string;
      mobileNumber?: string;
      postalAddress?: string;
      /** Same shape as POST /drivers; applied in one modify with vehicle fields. */
      newListedDriver?: {
        firstName: string;
        lastName: string;
        dateOfBirth: string;
        gender: 'MALE' | 'FEMALE';
        licenceAge: number;
        typeOfLicence?: string;
        numberOfClaims?: string;
        recentClaims?: 'YES' | 'NO';
        offroadTraining?: 'YES' | 'NO';
        yearsTowing?: number;
        towingTraining?: 'YES' | 'NO';
      };
      /** Update listed driver: match original name + same fields as newListedDriver. */
      updateListedDriver?: {
        matchFirstName: string;
        matchLastName: string;
        firstName: string;
        lastName: string;
        dateOfBirth: string;
        gender: 'MALE' | 'FEMALE';
        licenceAge: number;
        typeOfLicence?: string;
        numberOfClaims?: string;
        recentClaims?: 'YES' | 'NO';
        offroadTraining?: 'YES' | 'NO';
        yearsTowing?: number;
        towingTraining?: 'YES' | 'NO';
      };
    },
    getToken?: AccessTokenGetter
  ): Promise<{
    success: boolean;
    policyRef: string;
    registrationNumber: string;
    message?: string;
    code?: string;
    policyReferralBlocked?: boolean;
    alterationResult?: AlterationResult;
  }> {
    if (import.meta.env.DEV && isDevMode()) {
      clientLogDebug('[API] updateRegistrationNumber: dev mock (no bearer token / no backend)', policyRef);
      const { assertDevMockUpdateAllowed } = await loadDevPolicyScenarios();
      await assertDevMockUpdateAllowed(policyRef);
      applyDevMockPolicyUpdate(policyRef, body);
      return {
        success: true,
        policyRef,
        registrationNumber: body.registrationNumber,
        message: 'Updated (dev mock)',
      };
    }
    const url = `/policies/${encodeURIComponent(policyRef)}/update`;
    return apiRequest<{
      success: boolean;
      policyRef: string;
      registrationNumber: string;
      message?: string;
      code?: string;
      policyReferralBlocked?: boolean;
      alterationResult?: AlterationResult;
    }>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken
    );
  },

  /**
   * Add a listed driver. POST to /api/policies/{policyRef}/drivers.
   * Returns alterationResult for premium confirm step; then call bindAlteration to apply.
   */
  async addListedDriver(
    policyRef: string,
    body: {
      firstName: string;
      lastName: string;
      dateOfBirth: string; // YYYY-MM-DD
      gender: 'MALE' | 'FEMALE';
      licenceAge: number;
      typeOfLicence?: string;
      numberOfClaims?: string;
      recentClaims?: 'YES' | 'NO';
      recentSuspensions?: 'YES' | 'NO';
      offroadTraining?: 'YES' | 'NO';
      yearsTowing?: number;
      towingTraining?: 'YES' | 'NO';
    },
    getToken?: AccessTokenGetter
  ): Promise<{
    success: boolean;
    policyRef: string;
    message?: string;
    code?: string;
    policyReferralBlocked?: boolean;
    alterationResult?: AlterationResult;
  }> {
    if (import.meta.env.DEV && isDevMode()) {
      const { assertDevMockUpdateAllowed, getMockPolicyWithAccess } = await loadDevPolicyScenarios();
      await assertDevMockUpdateAllowed(policyRef);
      const existing = await getMockPolicyWithAccess(policyRef);
      applyDevMockPolicyUpdate(policyRef, {
        registrationNumber: existing.registrationNumber ?? 'ABC 123',
        newListedDriver: {
          firstName: body.firstName,
          lastName: body.lastName,
          dateOfBirth: body.dateOfBirth,
          gender: body.gender,
          licenceAge: body.licenceAge,
          typeOfLicence: body.typeOfLicence,
          numberOfClaims: body.numberOfClaims,
          recentClaims: body.recentClaims,
          offroadTraining: body.offroadTraining,
          yearsTowing: body.yearsTowing,
          towingTraining: body.towingTraining,
        },
      });
      return {
        success: true,
        policyRef,
        message: 'Driver added (dev mock)',
        alterationResult: {
          quoteReference: 'DEV-QUOTE',
          status: 'ACCEPTABLE',
          premiums: [],
          validationMessages: [],
          underwritingMessages: [],
        },
      };
    }
    const url = `/policies/${encodeURIComponent(policyRef)}/drivers`;
    return apiRequest<{
      success: boolean;
      policyRef: string;
      message?: string;
      code?: string;
      policyReferralBlocked?: boolean;
      alterationResult?: AlterationResult;
    }>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken
    );
  },

  /**
   * Bind (accept) an alteration quote. POST to /api/policies/{policyRef}/alteration/bind.
   */
  async bindAlteration(
    policyRef: string,
    body: { quoteReference: string },
    getToken?: AccessTokenGetter
  ): Promise<{ success: boolean; policyRef: string; message?: string }> {
    const url = `/policies/${encodeURIComponent(policyRef)}/alteration/bind`;
    clientLogDebug('[API] bindAlteration: POST', url, body);
    const result = await apiRequest<{ success: boolean; policyRef: string; message?: string }>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken
    );
    clientLogDebug('[API] bindAlteration response:', result);
    return result;
  },

  /**
   * Abandon (cancel) an alteration quote. POST to /api/policies/{policyRef}/alteration/abandon.
   */
  async abandonAlteration(
    policyRef: string,
    body: { quoteReference: string },
    getToken?: AccessTokenGetter
  ): Promise<{ success: boolean; policyRef: string; message?: string }> {
    const url = `/policies/${encodeURIComponent(policyRef)}/alteration/abandon`;
    return apiRequest<{ success: boolean; policyRef: string; message?: string }>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken
    );
  },

  /**
   * Start Paydock payment-method session (sets httpOnly cookie). Requires `credentials: 'include'`.
   * Returns `data.csrfToken` for subsequent POSTs in this flow.
   */
  async startPaymentMethodSession(
    policyRef: string,
    getToken?: AccessTokenGetter,
    init?: { signal?: AbortSignal }
  ): Promise<PaymentMethodStartResponse> {
    const url = `/policies/${encodeURIComponent(policyRef)}/payment-method/start`;
    return paymentMethodApiRequest<PaymentMethodStartResponse>(
      url,
      { method: 'POST', signal: init?.signal },
      getToken
    );
  },

  async sendPaymentMethod2fa(
    policyRef: string,
    body: { method: PaymentMethodOtpChannel },
    getToken?: AccessTokenGetter,
    csrfToken?: string | null
  ): Promise<PaymentMethodSimpleResponse> {
    const url = `/policies/${encodeURIComponent(policyRef)}/payment-method/send-2fa`;
    return paymentMethodApiRequest<PaymentMethodSimpleResponse>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      csrfToken
    );
  },

  async verifyPaymentMethod2fa(
    policyRef: string,
    body: { code: string },
    getToken?: AccessTokenGetter,
    csrfToken?: string | null
  ): Promise<PaymentMethodVerifyResponse> {
    const url = `/policies/${encodeURIComponent(policyRef)}/payment-method/verify-2fa`;
    return paymentMethodApiRequest<PaymentMethodVerifyResponse>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      csrfToken
    );
  },

  async updatePaymentMethodVault(
    policyRef: string,
    body: { token: string },
    getToken?: AccessTokenGetter,
    csrfToken?: string | null
  ): Promise<PaymentMethodSimpleResponse> {
    const url = `/policies/${encodeURIComponent(policyRef)}/payment-method/update`;
    return paymentMethodApiRequest<PaymentMethodSimpleResponse>(
      url,
      { method: 'POST', body: JSON.stringify(body) },
      getToken,
      csrfToken
    );
  },

  /**
   * Fetch a document URL with auth (e.g. policy document download). Returns raw Response for blob/arrayBuffer.
   */
  async fetchDocument(endpoint: string, getToken?: AccessTokenGetter): Promise<Response> {
    const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    const headers: HeadersInit = {};
    if (getToken) {
      const token = await getBearerAccessToken(getToken);
      if (token) headers['Authorization'] = `Bearer ${token}`;
    }
    await attachPortalIdTokenHeader(headers);
    return fetch(url, { method: 'GET', headers });
  },
};

/**
 * React hook for API calls with Auth0 bearer integration
 */
export function useApi() {
  const getTokenFromContext = useContext(AccessTokenContext);
  const getPortalIdTokenFromContext = useContext(PortalIdTokenContext);
  const getterRef = useRef<AccessTokenGetter>(async () => null);
  getterRef.current = async () => (getTokenFromContext ? await getTokenFromContext() : null);

  useLayoutEffect(() => {
    const getter = getPortalIdTokenFromContext
      ? () => getPortalIdTokenFromContext()
      : undefined;
    registerPortalIdTokenGetter(getter);
    return () => unregisterPortalIdTokenGetter(getter);
  }, [getPortalIdTokenFromContext]);

  return useMemo(
    () => ({
      getUser: () => api.getUser(getterRef.current),
      getHealth: () => api.getHealth(),
      getPolicies: () => api.getPolicies(getterRef.current),
      getPolicy: (policyRef: string) => api.getPolicy(policyRef, getterRef.current),
      submitWindscreenClaim: (body: SubmitWindscreenClaimPayload) =>
        api.submitWindscreenClaim(body, getterRef.current),
      submitMultiVehicleClaim: (body: SubmitMultiVehicleClaimPayload) =>
        api.submitMultiVehicleClaim(body, getterRef.current),
      submitWeatherClaim: (body: SubmitWeatherClaimPayload) =>
        api.submitWeatherClaim(body, getterRef.current),
      submitTheftClaim: (body: SubmitTheftClaimPayload) =>
        api.submitTheftClaim(body, getterRef.current),
      submitFireClaim: (body: SubmitFireClaimPayload) =>
        api.submitFireClaim(body, getterRef.current),
      submitMaliciousDamageClaim: (body: SubmitMaliciousDamageClaimPayload) =>
        api.submitMaliciousDamageClaim(body, getterRef.current),
      submitOtherLossClaim: (body: SubmitOtherLossClaimPayload) =>
        api.submitOtherLossClaim(body, getterRef.current),
      submitSingleVehicleClaim: (body: SubmitSingleVehicleClaimPayload) =>
        api.submitSingleVehicleClaim(body, getterRef.current),
      getSubmittedClaims: () => api.getSubmittedClaims(getterRef.current),
      getSubmittedClaimStatus: (claimNumber: string) =>
        api.getSubmittedClaimStatus(claimNumber, getterRef.current),
      linkPolicy: (body: Parameters<typeof api.linkPolicy>[0]) => api.linkPolicy(body, getterRef.current),
      verifyPolicyLinkOtp: (body: Parameters<typeof api.verifyPolicyLinkOtp>[0]) =>
        api.verifyPolicyLinkOtp(body, getterRef.current),
      resendPolicyLinkOtp: (body: Parameters<typeof api.resendPolicyLinkOtp>[0]) =>
        api.resendPolicyLinkOtp(body, getterRef.current),
      syncMfaPhone: () => api.syncMfaPhone(getterRef.current),
      updateRegistrationNumber: (policyRef: string, body: Parameters<typeof api.updateRegistrationNumber>[1]) =>
        api.updateRegistrationNumber(policyRef, body, getterRef.current),
      addListedDriver: (policyRef: string, body: Parameters<typeof api.addListedDriver>[1]) =>
        api.addListedDriver(policyRef, body, getterRef.current),
      bindAlteration: (policyRef: string, body: { quoteReference: string }) =>
        api.bindAlteration(policyRef, body, getterRef.current),
      abandonAlteration: (policyRef: string, body: { quoteReference: string }) =>
        api.abandonAlteration(policyRef, body, getterRef.current),
      startPaymentMethodSession: (policyRef: string, init?: { signal?: AbortSignal }) =>
        api.startPaymentMethodSession(policyRef, getterRef.current, init),
      sendPaymentMethod2fa: (
        policyRef: string,
        body: { method: PaymentMethodOtpChannel },
        csrfToken?: string | null
      ) => api.sendPaymentMethod2fa(policyRef, body, getterRef.current, csrfToken),
      verifyPaymentMethod2fa: (
        policyRef: string,
        body: { code: string },
        csrfToken?: string | null
      ) => api.verifyPaymentMethod2fa(policyRef, body, getterRef.current, csrfToken),
      updatePaymentMethodVault: (
        policyRef: string,
        body: { token: string },
        csrfToken?: string | null
      ) => api.updatePaymentMethodVault(policyRef, body, getterRef.current, csrfToken),
      fetchDocument: (endpoint: string) => api.fetchDocument(endpoint, getterRef.current),
    }),
    []
  );
}
